import { Outlet, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { PayrollCyclePageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import StatusModal from 'modules/payroll_bundle/components/ChangeStatusButton/StatusModal'
import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import {
  useMatchCyclePaymentUrl,
  useMatchCyclePaymentsGenerateUrl,
  useMatchCycleResultImportUrl,
  useMatchCycleResultUrl,
  useMatchCycleSupplementsImportUrl,
  useMatchPayrollPolicyCycleUrl,
} from 'modules/payroll_bundle/hooks/useMatchPayrollUrls'
import { usePayrollContext } from 'modules/payroll_bundle/lib/payrollContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import Footer from 'modules/payroll_bundle/pages/CyclesPage/Footer'
import NavigationTabs from 'modules/payroll_bundle/pages/CyclesPage/Header/NavigationTabs'
import { getTabs } from 'modules/payroll_bundle/pages/CyclesPage/Header/NavigationTabs/utils'
import payments from 'modules/payroll_bundle/store/collections/payments'
import resultsCollection from 'modules/payroll_bundle/store/collections/results'

import CyclesPageSubHeader from './Header'
import useHasNavigation from './useHasNavigation'

const CyclesPage = () => {
  const { navigation } = useNavigation()
  const { policyId: paramsPolicyId } = useParams()
  const payrollBundleContext = usePayrollBundlePageContext()

  const { selectedPolicy, policyPeriod } = payrollBundleContext

  const policyId = payrollBundleContext.selectedPolicy?.id
  const policyPeriodId = payrollBundleContext.policyPeriod?.id

  const { currentPeriod } = usePolicyPeriods()
  const status = policyPeriod?.attributes.status

  const hasNavigation = useHasNavigation(paramsPolicyId, currentPeriod?.id)
  const periodId = currentPeriod?.id
  const hasResults = resultsCollection.filterByPolicyPeriod(Number(periodId)).length > 0
  const hasPayments = payments.currentPeriodPayments(periodId).length > 0

  const { data } = useQuery(PayrollCyclePageDocument)

  const canChangePeriodStatus = Boolean(data?.payroll._policies.periodChangePayrollPeriodStatus.can)
  const canSeeCompanyPayrollResults = Boolean(
    data?.payroll._policies.resultsSeeCompanyPayrollResults.can
  )

  const tabs = useUnsafeMemo(
    () =>
      getTabs({
        navigation,
        hasResults,
        hasPayments,
        policyId: paramsPolicyId,
        periodId,
        seeCompanyPayrollResults: canSeeCompanyPayrollResults,
      }),
    [navigation, hasResults, hasPayments, paramsPolicyId, periodId, canSeeCompanyPayrollResults]
  )

  const policyIdString = String(policyId)
  const periodIdString = String(policyPeriodId)

  let returnTo = null
  let navigateTo = null

  const isCycleResulImportPage = useMatchCycleResultImportUrl(policyIdString, periodIdString)
  const isCyclePaymentsGeneterateUrl = useMatchCyclePaymentsGenerateUrl(
    policyIdString,
    periodIdString
  )
  const isCyclePaymentsPage = useMatchCyclePaymentUrl(policyIdString, periodIdString)
  const isCyleResultsPage = useMatchCycleResultUrl(policyIdString, periodIdString)
  const isCyleResultsImportPage = useMatchCycleResultImportUrl(policyIdString, periodIdString)
  const isCycleSupplementsImportPage = useMatchCycleSupplementsImportUrl(
    policyIdString,
    periodIdString
  )

  if (isCyclePaymentsPage) {
    returnTo = navigation.payrollBundle.policy.period.cycles.payments.fullPath({
      policyId,
      periodId: policyPeriodId,
    })

    navigateTo = navigation.payrollBundle.policy.period.cycles.payments.generate.fullPath({
      policyId,
      periodId: policyPeriodId,
    })
  } else if (isCyleResultsPage || isCyleResultsImportPage) {
    returnTo = navigation.payrollBundle.policy.period.cycles.results.fullPath({
      policyId,
      periodId: policyPeriodId,
    })

    navigateTo = navigation.payrollBundle.policy.period.cycles.results.import.fullPath({
      policyId,
      periodId: policyPeriodId,
    })
  }

  const isCyclePage = useMatchPayrollPolicyCycleUrl(
    String(payrollBundleContext.selectedPolicy?.id),
    String(payrollBundleContext.policyPeriod?.id)
  )

  const { showChangeStatusModal: ctxShowChangeStatusModal } = usePayrollContext()
  const showChangeStatusModal = ctxShowChangeStatusModal && !!selectedPolicy?.id

  const showFooter = hasNavigation && isCyclePage && !!status && canChangePeriodStatus

  const displaySubHeader =
    !isCycleResulImportPage && !isCyclePaymentsGeneterateUrl && !isCycleSupplementsImportPage

  const footer = Footer({
    status,
    policyId,
    periodId: policyPeriodId,
    isVisible: showFooter,
  })

  return (
    <ResponsivePage.Body showHeader={false} variant="full" {...footer} loading={false} error={null}>
      {displaySubHeader && <CyclesPageSubHeader periodId={periodId} policyId={paramsPolicyId} />}
      {hasNavigation && (
        <Box paddingTop="s16">
          <NavigationTabs
            tabs={tabs}
            periodId={periodId}
            policyId={paramsPolicyId}
            isOffcycle={false}
          />
        </Box>
      )}
      <Outlet
        context={{
          returnTo,
          navigateTo,
          ...payrollBundleContext,
        }}
      />
      {showChangeStatusModal && policyPeriod && <StatusModal policyPeriod={policyPeriod} />}
    </ResponsivePage.Body>
  )
}

export default observer(CyclesPage)
